import {SelectItem} from '@/components/ui/select'
import {useI18n} from '@/i18n'
import type {EnumValue} from '@/components/shared/EnumValue'

type I18n = ReturnType<typeof useI18n>

interface EnumSelectItemsProps<T extends EnumValue> {
    /**
     * The enum class. Must expose a `values: ReadonlyArray<T>` member — every
     * `EnumValue` subclass in `frontend/src/types/` follows this convention.
     */
    enumClass: { values: ReadonlyArray<T> }
    /**
     * Restrict the rendered set to these specific enum values (preserving
     * `enumClass.values` order). Use in forms that only accept a subset of
     * the enum — e.g. `AuthType` per connector type.
     */
    include?: ReadonlyArray<T>
    /**
     * Predicate-based subset filter. Runs after `include`. Use when the
     * allowed subset is computed (permissions, feature flags, etc.).
     */
    filter?: (value: T) => boolean
    /**
     * Optional label resolver. Defaults to `v.label(i18n)`. Pass e.g.
     * `(v, i18n) => v.shortLabel(i18n)` for subclasses that carry a shorter
     * picker label alongside the primary label.
     */
    labelFn?: (value: T, i18n: I18n) => string
    /**
     * When set, prepends an "All" sentinel `<SelectItem>` with this label.
     * Use in optional pickers (e.g. filter Selects where "no selection"
     * means "match everything"). Omit in forms that require a concrete
     * choice — those should use `<Select required>` on the parent.
     */
    allLabel?: string
    /** Value for the "All" sentinel. Defaults to `"all"`. */
    allValue?: string
    /**
     * Optional per-option test-id prefix — each SelectItem gets
     * `${prefix}-${code}`. The "All" sentinel gets `${prefix}-${allValue}`.
     * When omitted, no test-id is set.
     */
    testIdPrefix?: string
}

/**
 * Render a `<SelectItem>` per entry in an `EnumValue` subclass's `values` array,
 * using the enum's own `label(i18n)` (or a caller-supplied resolver) for the
 * display text. Paired inside shadcn's `<SelectContent>`:
 *
 * ```tsx
 * <Select value={mode} onValueChange={setMode}>
 *     <SelectTrigger><SelectValue/></SelectTrigger>
 *     <SelectContent>
 *         <EnumSelectItems enumClass={EnforcementMode}/>
 *     </SelectContent>
 * </Select>
 * ```
 *
 * Subset:
 *
 * ```tsx
 * <EnumSelectItems enumClass={AuthType} include={[AuthType.BEARER_TOKEN, AuthType.BASIC_AUTH]}/>
 * ```
 *
 * With "All" sentinel (for filter-style Selects):
 *
 * ```tsx
 * <EnumSelectItems enumClass={Status} allLabel={i18n.common.all}/>
 * ```
 *
 * The enum becomes the single source of truth for both Badge rendering AND
 * Select option rendering. Adding a new enum value automatically surfaces in
 * every Select that uses this helper — no page-by-page updates required.
 */
 
const DEFAULT_ALL_VALUE = 'all'
 

export function EnumSelectItems<T extends EnumValue>({
    enumClass,
    include,
    filter,
    labelFn,
    allLabel,
    allValue = DEFAULT_ALL_VALUE,
    testIdPrefix,
}: EnumSelectItemsProps<T>) {
    const i18n = useI18n()

    const included = include ? new Set(include) : null
    const items = enumClass.values.filter(v => {
        if (included && !included.has(v)) return false
        if (filter && !filter(v)) return false
        return true
    })

    return (
        <>
            {allLabel !== undefined && (
                <SelectItem
                    key={allValue}
                    value={allValue}
                     
                    data-testid={testIdPrefix ? `${testIdPrefix}-${allValue}` : undefined}
                >
                    {allLabel}
                </SelectItem>
            )}
            {items.map(v => (
                <SelectItem
                    key={v.code}
                    value={v.code}
                     
                    data-testid={testIdPrefix ? `${testIdPrefix}-${v.code}` : undefined}
                >
                    {labelFn ? labelFn(v, i18n) : v.label(i18n)}
                </SelectItem>
            ))}
        </>
    )
}
